import {
  Button,
  Modal,
  SegmentedControl,
  Select,
  Skeleton,
  Textarea,
  VisuallyHidden,
} from '@mantine/core';
import { IconDownload, IconHistory, IconPencil, IconPlus, IconX } from '@tabler/icons-react';
import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import type { ReportBrand } from '../voyage/source.js';
import classes from './CrewNotesPanel.module.css';
import {
  CREW_NOTE_KINDS,
  type CrewNote,
  type CrewNoteFilter,
  type CrewNoteKind,
  type CrewNoteList,
  type CrewNotesSource,
  type CrewNoteVersion,
  contractOptions,
  emptyNoteForm,
  filterNotes,
  type NoteForm,
  noteFormBody,
  noteFormFrom,
} from './crew-notes.js';
import { crewNotesFileName, crewNotesPdf } from './crew-notes-pdf.js';
import { browserPdfOutput, type PdfOutput, renderPdf } from './pdf.js';
import { errorCode } from './source.js';
import type { CrewProfileView } from './types.js';

const KIND_TONE: Record<CrewNoteKind, StatusTone> = {
  evaluation: 'info',
  feedback: 'success',
  note: 'neutral',
};

function useNoteText() {
  const { t } = useTranslation();
  return (key: string, options: Record<string, unknown> = {}) => t(`crewNotes.${key}`, options);
}

/** Yazan ve zaman: "Ad · GG.AA.YYYY SS:DD" (yerel saat). */
function byLine(k: ReturnType<typeof useNoteText>, name: string | null, at: string): string {
  return k('meta.by', { name: name ?? k('meta.unknownAuthor'), date: formatDateTime(at) });
}

function VersionRow({ version }: { version: CrewNoteVersion }) {
  const k = useNoteText();
  return (
    <li className={classes.version}>
      <span className={classes.meta}>
        <strong>{k('history.version', { version: version.versionNo })}</strong>
        <span>{k(`history.change.${version.change}`)}</span>
        <span>{k(`kind.${version.kind}`)}</span>
        <span>{byLine(k, version.createdByName, version.createdAt)}</span>
      </span>
      <p className={classes.versionText}>{version.body}</p>
      {version.reason ? (
        <p className={classes.reason}>{k('meta.correctionReason', { reason: version.reason })}</p>
      ) : null}
    </li>
  );
}

function NoteItem({
  note,
  canCorrect,
  canWithdraw,
  onCorrect,
  onWithdraw,
}: {
  note: CrewNote;
  canCorrect: boolean;
  canWithdraw: boolean;
  onCorrect: (note: CrewNote) => void;
  onWithdraw: (note: CrewNote) => void;
}) {
  const k = useNoteText();
  const [history, setHistory] = useState(false);
  const current = note.current;
  const contract =
    current.assignmentFrom !== null
      ? k('meta.contract', {
          from: formatDay(current.assignmentFrom),
          to: current.assignmentTo ? formatDay(current.assignmentTo) : k('meta.contractOpen'),
        })
      : null;
  const open = !note.withdrawn;
  return (
    <li className={classes.note} data-withdrawn={note.withdrawn || undefined}>
      <div className={classes.head}>
        <span className={classes.badges}>
          <StatusBadge tone={KIND_TONE[current.kind]}>{k(`kind.${current.kind}`)}</StatusBadge>
          {note.withdrawn ? (
            <StatusBadge tone="danger" variant="dot">
              {k('meta.withdrawn')}
            </StatusBadge>
          ) : current.versionNo > 1 ? (
            <StatusBadge tone="warning" variant="dot">
              {k('meta.corrected', { version: current.versionNo })}
            </StatusBadge>
          ) : null}
        </span>
        <span className={classes.actions}>
          {open && canCorrect ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconPencil size={14} aria-hidden />}
              onClick={() => onCorrect(note)}
            >
              {k('actions.correct')}
            </Button>
          ) : null}
          {open && canWithdraw ? (
            <Button
              size="xs"
              variant="default"
              color="red"
              leftSection={<IconX size={14} aria-hidden />}
              onClick={() => onWithdraw(note)}
            >
              {k('actions.withdraw')}
            </Button>
          ) : null}
          {note.versions.length > 1 ? (
            <Button
              size="xs"
              variant="subtle"
              leftSection={<IconHistory size={14} aria-hidden />}
              aria-expanded={history}
              onClick={() => setHistory((value) => !value)}
            >
              {k(history ? 'actions.hideHistory' : 'actions.history')}
            </Button>
          ) : null}
        </span>
      </div>
      <p className={classes.body}>{current.body}</p>
      {note.withdrawn && current.reason ? (
        <p className={classes.reason}>
          {k('meta.withdrawnReason', {
            name: current.createdByName ?? k('meta.unknownAuthor'),
            date: formatDateTime(current.createdAt),
            reason: current.reason,
          })}
        </p>
      ) : null}
      <p className={classes.meta}>
        <span>{byLine(k, note.createdByName, note.createdAt)}</span>
        {current.vesselName ? <span>{k('meta.vessel', { name: current.vesselName })}</span> : null}
        {contract ? <span>{contract}</span> : null}
      </p>
      {history ? (
        <ul className={classes.history} aria-label={k('history.title')}>
          {note.versions.map((version) => (
            <VersionRow key={version.id} version={version} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/** Not listesi (sunum): en yeni üstte; süzgece göre; düğmeler yetkiye göre. */
export function CrewNotesList({
  notes,
  filter,
  canCorrect,
  canWithdraw,
  onCorrect,
  onWithdraw,
}: {
  notes: readonly CrewNote[];
  filter: CrewNoteFilter;
  canCorrect: boolean;
  canWithdraw: boolean;
  onCorrect: (note: CrewNote) => void;
  onWithdraw: (note: CrewNote) => void;
}) {
  const k = useNoteText();
  const shown = filterNotes(notes, filter);
  if (shown.length === 0) {
    return <EmptyState title={k(notes.length === 0 ? 'empty' : 'emptyFiltered')} />;
  }
  return (
    <ul className={classes.list}>
      {shown.map((note) => (
        <NoteItem
          key={note.id}
          note={note}
          canCorrect={canCorrect}
          canWithdraw={canWithdraw}
          onCorrect={onCorrect}
          onWithdraw={onWithdraw}
        />
      ))}
    </ul>
  );
}

function useSaveError() {
  const k = useNoteText();
  const { i18n } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    return i18n.exists(`crewNotes.errors.${code}`) ? k(`errors.${code}`) : k('errors.GENERIC');
  };
}

function NoteDialog({
  title,
  submit,
  ready,
  danger,
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  submit: string;
  ready: boolean;
  danger?: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  children: ReactNode;
}) {
  const k = useNoteText();
  const { t } = useTranslation();
  const errorText = useSaveError();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={title}
      size="lg"
    >
      <form className={classes.form} onSubmit={send}>
        {children}
        {error ? (
          <p className={classes.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.formActions}>
          <Button variant="default" onClick={onClose}>
            {k('form.cancel')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={!ready}
            {...(danger ? { color: 'red' } : {})}
          >
            {submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function NoteFields({
  form,
  onChange,
  view,
  vessels,
  correction,
}: {
  form: NoteForm;
  onChange: (form: NoteForm) => void;
  view: CrewProfileView;
  vessels: readonly { value: string; label: string }[];
  correction: boolean;
}) {
  const k = useNoteText();
  const contracts = contractOptions(view.assignments, k('meta.contractOpen'));
  const none = { value: '', label: k('form.none') };
  // Kişinin atamalarındaki gemiler de seçilebilir (filo listesi okunamasa da)
  const vesselOptions = [
    none,
    ...vessels,
    ...view.assignments
      .filter((a) => !vessels.some((v) => v.value === a.vesselId))
      .map((a) => ({ value: a.vesselId, label: a.vesselName })),
  ].filter((option, index, all) => all.findIndex((o) => o.value === option.value) === index);
  return (
    <>
      <Select
        label={k('form.kind')}
        required
        allowDeselect={false}
        data={CREW_NOTE_KINDS.map((kind) => ({ value: kind, label: k(`kind.${kind}`) }))}
        value={form.kind}
        onChange={(value) => value && onChange({ ...form, kind: value as CrewNoteKind })}
      />
      <Textarea
        label={k('form.body')}
        description={k('form.bodyHelp')}
        required
        autosize
        minRows={4}
        maxRows={14}
        maxLength={4000}
        value={form.body}
        onChange={(event) => onChange({ ...form, body: event.currentTarget.value })}
      />
      <Select
        label={k('form.contract')}
        data={[none, ...contracts.map(({ value, label }) => ({ value, label }))]}
        value={form.assignmentId}
        allowDeselect={false}
        onChange={(value) => {
          const contract = contracts.find((c) => c.value === value);
          onChange({
            ...form,
            assignmentId: value ?? '',
            vesselId: contract ? contract.vesselId : form.vesselId,
          });
        }}
      />
      <Select
        label={k('form.vessel')}
        data={vesselOptions}
        value={form.vesselId}
        allowDeselect={false}
        searchable
        onChange={(value) => {
          const vesselId = value ?? '';
          const contract = contracts.find((c) => c.value === form.assignmentId);
          // Başka gemi seçilince o gemiye ait olmayan kontrat bağı düşer
          onChange({
            ...form,
            vesselId,
            assignmentId: contract && contract.vesselId !== vesselId ? '' : form.assignmentId,
          });
        }}
      />
      {correction ? (
        <Textarea
          label={k('form.reason')}
          autosize
          minRows={2}
          maxLength={500}
          value={form.reason}
          onChange={(event) => onChange({ ...form, reason: event.currentTarget.value })}
        />
      ) : null}
    </>
  );
}

type Dialog =
  | { kind: 'add'; form: NoteForm }
  | { kind: 'correct'; note: CrewNote; form: NoteForm }
  | { kind: 'withdraw'; note: CrewNote; reason: string };

/**
 * Crew Profile ▸ Office notes (ADR 0159; sahip 2026-11-04 B4): yalnız web ofiste ve
 * `crew_notes.view` hücresiyle açılır. Not türüne göre süzme, yeni not, düzeltme (yeni sürüm),
 * gerekçeli geri çekme, geçmiş ve KVKK m.11 dışa aktarımı (PDF).
 */
export function CrewNotesPanel({
  source,
  view,
  loadVessels,
  brand,
  pdf,
}: {
  source: CrewNotesSource;
  view: CrewProfileView;
  loadVessels?: () => Promise<{ id: string; name: string }[]>;
  brand?: () => Promise<ReportBrand | null>;
  pdf?: PdfOutput;
}) {
  const k = useNoteText();
  const { t } = useTranslation();
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; list: CrewNoteList }
  >({ kind: 'loading' });
  const [filter, setFilter] = useState<CrewNoteFilter>('all');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [vessels, setVessels] = useState<{ value: string; label: string }[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    source
      .list(view.userId)
      .then((list) => setState({ kind: 'ready', list }))
      .catch(() => setState({ kind: 'error' }));
  }, [source, view.userId]);
  useEffect(load, [load]);

  const openForm = (next: Dialog) => {
    setDialog(next);
    if (vessels.length === 0 && loadVessels) {
      loadVessels()
        .then((list) => setVessels(list.map((v) => ({ value: v.id, label: v.name }))))
        .catch(() => setVessels([]));
    }
  };
  const saved = () => {
    setDialog(null);
    load();
  };

  const exportPdf = async (notes: readonly CrewNote[]) => {
    setExporting(true);
    setExportError(null);
    try {
      const definition = crewNotesPdf((key, options) => String(t(key, options ?? {})), {
        view,
        notes,
        brand: (await brand?.().catch(() => null)) ?? null,
        printedAt: new Date().toISOString(),
      });
      await (pdf ?? browserPdfOutput()).save(crewNotesFileName(view), () => renderPdf(definition));
    } catch {
      setExportError(k('exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  if (state.kind === 'loading') {
    return (
      <div className={classes.root} aria-busy>
        <VisuallyHidden>{k('loading')}</VisuallyHidden>
        <Skeleton height={36} radius="md" />
        <Skeleton height={120} radius="md" />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <EmptyState
        tone="danger"
        title={k('error')}
        action={
          <Button size="xs" variant="light" onClick={load}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  }
  const { list } = state;
  const ready = dialog && dialog.kind !== 'withdraw' ? noteFormBody(dialog.form) : null;

  return (
    <div className={classes.root}>
      <p className={classes.intro}>{k('intro')}</p>
      <div className={classes.toolbar}>
        <SegmentedControl
          size="xs"
          aria-label={k('filter.label')}
          value={filter}
          onChange={(value) => setFilter(value as CrewNoteFilter)}
          data={[
            { value: 'all', label: k('filter.all') },
            ...CREW_NOTE_KINDS.map((kind) => ({ value: kind, label: k(`kind.${kind}`) })),
          ]}
        />
        <span className={classes.tools}>
          {list.canExport ? (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconDownload size={14} aria-hidden />}
              loading={exporting}
              onClick={() => void exportPdf(list.notes)}
            >
              {k('export')}
            </Button>
          ) : null}
          {list.canCreate ? (
            <Button
              size="xs"
              leftSection={<IconPlus size={14} aria-hidden />}
              onClick={() => openForm({ kind: 'add', form: emptyNoteForm() })}
            >
              {k('add')}
            </Button>
          ) : null}
        </span>
      </div>
      {exportError ? (
        <p className={classes.error} role="alert">
          {exportError}
        </p>
      ) : null}
      <CrewNotesList
        notes={list.notes}
        filter={filter}
        canCorrect={list.canCorrect}
        canWithdraw={list.canWithdraw}
        onCorrect={(note) => openForm({ kind: 'correct', note, form: noteFormFrom(note) })}
        onWithdraw={(note) => setDialog({ kind: 'withdraw', note, reason: '' })}
      />
      {dialog?.kind === 'add' ? (
        <NoteDialog
          title={k('form.addTitle')}
          submit={k('form.save')}
          ready={ready !== null}
          onClose={() => setDialog(null)}
          onSubmit={async () => {
            if (!ready) return;
            await source.create(view.userId, ready);
            saved();
          }}
        >
          <NoteFields
            form={dialog.form}
            onChange={(form) => setDialog({ ...dialog, form })}
            view={view}
            vessels={vessels}
            correction={false}
          />
        </NoteDialog>
      ) : null}
      {dialog?.kind === 'correct' ? (
        <NoteDialog
          title={k('form.correctTitle')}
          submit={k('form.saveCorrection')}
          ready={ready !== null}
          onClose={() => setDialog(null)}
          onSubmit={async () => {
            if (!ready) return;
            await source.correct(dialog.note.id, dialog.note.current.versionNo, {
              ...ready,
              reason: dialog.form.reason.trim() || null,
            });
            saved();
          }}
        >
          <NoteFields
            form={dialog.form}
            onChange={(form) => setDialog({ ...dialog, form })}
            view={view}
            vessels={vessels}
            correction
          />
        </NoteDialog>
      ) : null}
      {dialog?.kind === 'withdraw' ? (
        <NoteDialog
          title={k('form.withdrawTitle')}
          submit={k('form.confirmWithdraw')}
          ready={dialog.reason.trim() !== ''}
          danger
          onClose={() => setDialog(null)}
          onSubmit={async () => {
            await source.withdraw(
              dialog.note.id,
              dialog.note.current.versionNo,
              dialog.reason.trim(),
            );
            saved();
          }}
        >
          <p className={classes.help}>{k('form.withdrawHelp')}</p>
          <Textarea
            label={k('form.withdrawReason')}
            required
            autosize
            minRows={2}
            maxLength={500}
            value={dialog.reason}
            onChange={(event) => setDialog({ ...dialog, reason: event.currentTarget.value })}
          />
        </NoteDialog>
      ) : null}
    </div>
  );
}
